import type { MouseEvent, ReactElement, RefObject } from "react";
import { Layers } from "lucide-react";
import { Avatar } from "@primitives/Avatar";
import { Popover } from "@primitives/overlay";
import { Text } from "@primitives/Text";
import type { QueueSummary } from "../../../experience-shell/queues/types";
import { formatAncestorPath, type QueueNameResolver } from "../workflowRecipients";
import { optionDomId } from "./optionDomId";
import { displayNameFor, type RecipientCandidates } from "./recipients";
import styles from "./RecipientListbox.module.css";

/**
 * RecipientListbox: the floating suggestion list shared by the "To" field
 * and the `@` mention in the question.
 *
 * Focus never enters the list. The owning input keeps focus and points
 * `aria-activedescendant` at the highlighted option. Rows are plain
 * elements, so a pointer-down on one does not move focus.
 */

// Six 36px rows plus two group labels plus padding. Longer results scroll.
const LIST_MAX_HEIGHT = 6 * 36 + 2 * 28 + 8;

export interface RecipientListboxProps {
  readonly id: string;
  readonly open: boolean;
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly matchAnchorWidth: boolean;
  readonly candidates: RecipientCandidates;
  readonly highlightedId: string | undefined;
  /** True while the queue tree has not arrived. */
  readonly loading: boolean;
  readonly resolveQueueName: QueueNameResolver;
  readonly onPick: (queue: QueueSummary) => void;
  readonly onHighlight: (queueId: string) => void;
  readonly onClose: () => void;
}

function keepFocus(event: MouseEvent): void {
  event.preventDefault();
}

interface OptionProps {
  readonly listId: string;
  readonly queue: QueueSummary;
  readonly highlighted: boolean;
  readonly resolveQueueName: QueueNameResolver;
  readonly onPick: (queue: QueueSummary) => void;
  readonly onHighlight: (queueId: string) => void;
}

function Option({
  listId,
  queue,
  highlighted,
  resolveQueueName,
  onPick,
  onHighlight,
}: OptionProps): ReactElement {
  const name = displayNameFor(queue);
  const path = queue.isPersonal ? null : formatAncestorPath(queue.ancestorPath, resolveQueueName);
  return (
    <div
      id={optionDomId(listId, queue.id)}
      role="option"
      aria-selected={highlighted}
      className={styles.option}
      onMouseDown={keepFocus}
      onMouseMove={() => {
        if (!highlighted) onHighlight(queue.id);
      }}
      onClick={() => {
        onPick(queue);
      }}
    >
      {queue.isPersonal ? (
        <Avatar alt={name} size="sm" />
      ) : (
        <span aria-hidden="true" className={styles.queueGlyph}>
          <Layers size={14} strokeWidth={2} />
        </span>
      )}
      <span className={styles.text}>
        <Text role="body" truncate>
          {name}
        </Text>
        {path !== null && (
          <Text role="caption" tone="muted" truncate>
            {path}
          </Text>
        )}
      </span>
    </div>
  );
}

interface GroupProps extends Omit<OptionProps, "queue" | "highlighted"> {
  readonly label: string;
  readonly items: readonly QueueSummary[];
  readonly highlightedId: string | undefined;
}

// A group with no matches hides instead of leaving, so the other group's
// options keep their DOM while the query narrows or a pick removes a row.
function Group({ label, items, highlightedId, ...option }: GroupProps): ReactElement {
  return (
    <div role="group" aria-label={label} hidden={items.length === 0}>
      <div className={styles.groupLabel}>
        <Text role="caption" tone="subtle" weight="medium">
          {label}
        </Text>
      </div>
      {items.map((q) => (
        <Option key={q.id} queue={q} highlighted={q.id === highlightedId} {...option} />
      ))}
    </div>
  );
}

export function RecipientListbox({
  id,
  open,
  anchorRef,
  matchAnchorWidth,
  candidates,
  highlightedId,
  loading,
  resolveQueueName,
  onPick,
  onHighlight,
  onClose,
}: RecipientListboxProps): ReactElement {
  const empty = candidates.people.length === 0 && candidates.queues.length === 0;
  const option = { listId: id, resolveQueueName, onPick, onHighlight };
  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      id={id}
      role="listbox"
      ariaLabel="People and queues"
      placement="bottom-start"
      matchAnchorWidth={matchAnchorWidth}
      {...(matchAnchorWidth ? {} : { minWidth: 320 })}
      maxHeight={LIST_MAX_HEIGHT}
      initialFocus="none"
      returnFocus={false}
      testId="recipient-listbox"
    >
      <div className={styles.list}>
        {empty && (
          <div className={styles.empty}>
            <Text role="bodySm" tone="subtle">
              {loading ? "Loading people and queues\u2026" : "No matches"}
            </Text>
          </div>
        )}
        <Group
          label="People"
          items={candidates.people}
          highlightedId={highlightedId}
          {...option}
        />
        <Group
          label="Queues"
          items={candidates.queues}
          highlightedId={highlightedId}
          {...option}
        />
      </div>
    </Popover>
  );
}
